/**
 * DEV needle-cloud services PROXIED through the editor origin (edit-play-mode
 * plan 5b, the stage-2 hole): the local cloud stack writes absolute
 * `http://localhost:5500/...` urls into its responses, which only resolve on
 * the dev machine itself — a phone opening `https://<LAN-IP>:5174/play` asks
 * ITSELF for localhost. The editor's dev server proxies these path prefixes to
 * the local services (see platform.pwa vite.config "server.proxy" — it mirrors
 * THIS list), and the document WRITER folds matching absolute srcs into the
 * host-relative prefix form, which resolves against whatever origin serves the
 * document (desktop editor, LAN play page, deployed host alike).
 *
 * Deliberately NOT solved by making the cloud server emit relative urls: its
 * `extractCDNKeyFromURL` prefix-strips incoming urls against the ABSOLUTE base,
 * so a relative base would break key extraction for every legacy absolute url.
 */
export const DEV_CLOUD_PROXY: ReadonlyArray<{ prefix: string; target: string }> = [
    { prefix: "/-/assets/", target: "http://localhost:5500/-/assets/" },
    { prefix: "/-/media/", target: "http://localhost:5500/-/media/" },
];

/**
 * Absolute cloud url → host-relative proxied form ("/-/assets/…"); other srcs
 * pass through unchanged. Folds BOTH absolute forms of the same file:
 * - the dev service's own url (`http://localhost:5500/-/assets/…`)
 * - the PAGE-ORIGIN url (`https://localhost:5174/-/assets/…` — what a proxied
 *   fetch or an origin-resolved recorded src carries)
 * Self-limiting by prefix (the forms only exist where the proxy exists), so
 * callers need no environment gate — and every consumer of a src key MUST fold
 * before comparing/storing, or the three forms of one file stop matching
 * (real case: identities registered under one form, looked up under another —
 * dropped assets "forgot" their library origin after a save).
 */
export function foldDevCloudSrc(src: string): string {
    const origin = typeof location !== "undefined" ? location.origin : undefined;
    for (const { prefix, target } of DEV_CLOUD_PROXY) {
        if (src.startsWith(target)) return prefix + src.substring(target.length);
        if (origin && src.startsWith(origin + prefix)) return src.substring(origin.length);
    }
    return src;
}

/** is this src/uri a needle-cloud-served asset location, in ANY of its forms
 * (remote:// symbolic, prod cloud, dev service, host-relative/page-origin
 * dev proxy)? Fold-first, per the rule above. */
export function isCloudAssetSrc(src: string | null | undefined): boolean {
    if (!src) return false;
    if (src.startsWith("remote://")) return true;
    const folded = foldDevCloudSrc(src);
    if (folded !== src) return true; // a dev service/page-origin form folded
    if (folded.startsWith("https://cloud.needle.tools/")) return true;
    for (const { prefix } of DEV_CLOUD_PROXY) {
        if (folded.startsWith(prefix)) return true; // already host-relative
    }
    return false;
}
